import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Mic, PawPrint, Mountain, Sun, CloudRain, Zap, X, Filter, Search, Volume2, CheckCircle, Home, PlayCircle, PauseCircle,
    Minimize,
    Maximize,
    OctagonX
} from "lucide-react"; 


import lion from "../Photos/AnimalSound/lion.png"
import frog from "../Photos/AnimalSound/frog.png"
import parrot from "../Photos/AnimalSound/parrot.png"
import whale from "../Photos/AnimalSound/whale.png"
import wolf from "../Photos/AnimalSound/wolf.png"
import owl from "../Photos/AnimalSound/owl.png"

import lionvideo from "../components/Videos/TheSilentTree.mp4"



// --- Video/Sound Data (IMPORTANT: REPLACE PLACEHOLDERS) ---
// For this to work, you MUST use actual paths for sound and video sources.
// I'm using YouTube embeds for video for easy demonstration.
const ALL_ANIMAL_SOUNDS = [
    {
        id: 101,
        name: "Lion Roar",
        description: "The loud, deep vocalization of a male lion, asserting territory.",
        habitat: "Savanna",
        type: "Mammal",
        icon: Mountain,
        difficulty: "Loud",
        imageUrl: lion, // Static image fallback
        soundUrl: "/sounds/lion_roar.mp3", // <<< REPLACE WITH ACTUAL MP3 PATH
        videoUrl: lionvideo // <<< REPLACE WITH ACTUAL VIDEO URL
    },
    {
        id: 102,
        name: "Frog Croak",
        description: "A rhythmic sound made by male frogs to attract mates.",
        habitat: "Wetlands",
        type: "Amphibian",
        icon: CloudRain,
        difficulty: "Subtle",
        imageUrl: frog,
        soundUrl: "/sounds/frog_croak.mp3",
        videoUrl: "https://www.youtube.com/embed/pC758i3j_tU?autoplay=1&mute=1&controls=0", 
    },
    // ... rest of your animal data ... (Ensure soundUrl and videoUrl are present)
    {
        id: 103,
        name: "Whale Song",
        description: "Complex, haunting sounds produced by humpback whales for communication.",
        habitat: "Ocean",
        type: "Marine",
        icon: Zap,
        difficulty: "Complex",
        imageUrl: whale,
        soundUrl: "/sounds/whale_song.mp3",
        videoUrl: "https://www.youtube.com/embed/i0-H65-1d0w?autoplay=1&mute=1&controls=0",
    },
    {
        id: 104,
        name: "Wolf Howl",
        description: "A long, mournful sound used to locate pack members or mark territory.",
        habitat: "Forest",
        type: "Mammal",
        icon: Mountain,
        difficulty: "Loud",
        imageUrl: wolf,
        soundUrl: "/sounds/wolf_howl.mp3",
        videoUrl: "https://www.youtube.com/embed/x-64CaD8GXg?autoplay=1&mute=1&controls=0",
    },
    {
        id: 105,
        name: "Parrot Talk",
        description: "Mimicry and squawks used by parrots to communicate and bond.",
        habitat: "Jungle",
        type: "Bird",
        icon: Sun,
        difficulty: "Complex",
        imageUrl: parrot,
        soundUrl: "/sounds/parrot_talk.mp3",
        videoUrl: "https://www.youtube.com/embed/p1t_o7aB4i8?autoplay=1&mute=1&controls=0",
    },
    {
        id: 106,
        name: "Owl Hoot",
        description: "The classic call of an owl, often used for communication at night.",
        habitat: "Forest",
        type: "Bird",
        icon: Mountain,
        difficulty: "Subtle",
        imageUrl: owl,
        soundUrl: "/sounds/owl_hoot.mp3",
        videoUrl: "https://www.youtube.com/embed/c-tXU-FkMh0?autoplay=1&mute=1&controls=0",
    },
];

const ANIMAL_CATEGORIES = [
    { name: "All Animals", icon: Home, filterKey: null },
    { name: "Mammal", icon: PawPrint, filterKey: "Mammal" },
    { name: "Bird", icon: Sun, filterKey: "Bird" },
    { name: "Amphibian", icon: CloudRain, filterKey: "Amphibian" },
    { name: "Marine", icon: Zap, filterKey: "Marine" },
];

const containerVariants = {
    // ... (Framer Motion Variants remain the same)
    hidden: { opacity: 0 },
    visible: { 
        opacity: 1, 
        transition: { 
            staggerChildren: 0.08, 
            when: "beforeChildren" 
        } 
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: { 
            duration: 0.5, 
            ease: "easeOut"
        } 
    },
};

const sidebarItemVariants = {
    active: { scale: 1.05, background: "rgba(3, 105, 161, 0.1)", color: "#0369A1", transition: { type: "spring", stiffness: 300 } },
    inactive: { scale: 1, background: "rgba(255, 255, 255, 0)", color: "#4B5563" },
};


// --- AnimalSoundCard Component (Refined for Sound and Video) ---
// ... (imports and data definitions remain the same) ...

// --- AnimalSoundCard Component (CORRECTED for variable scoping) ---
const AnimalSoundCard = ({ animal }) => {
    const IconComponent = animal.icon;
    const [isPlaying, setIsPlaying] = useState(false); // Controls Video Visibility
    const [isFullscreen, setIsFullscreen] = useState(false); 
    const audioRef = useRef(null);
    const cardRef = useRef(null);

    // FIX: Declare and initialize variables here so they are in scope
    let levelClasses = "font-semibold text-xs rounded-lg px-2 py-1";
    let buttonColor = "bg-teal-600 hover:bg-teal-700 shadow-teal-300/50";
    
    // Dynamic color for sound impact level (This logic modifies the declared variables)
    if (animal.difficulty === "Loud") {
        levelClasses += " bg-red-500 text-white";
    }
    else if (animal.difficulty === "Complex") {
        levelClasses += " bg-indigo-500 text-white";
    }
    else if (animal.difficulty === "Subtle") {
        levelClasses += " bg-green-500 text-white";
    }

    // Initialize Audio element once
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio(animal.soundUrl);
            audioRef.current.onended = () => setIsPlaying(false);
            audioRef.current.onerror = () => {
                console.error(`Error loading sound for ${animal.name}`);
                setIsPlaying(false);
            };
        }
    }, [animal.soundUrl, animal.name]);

    // Handle closing fullscreen with ESC key
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullscreen]);


    // --- Function to ONLY toggle Audio playback ---
    const handleToggleSound = () => {
        if (!audioRef.current) return;

        // Check if sound is currently playing (separate state from `isPlaying` which controls video)
        const isSoundPlaying = !audioRef.current.paused;

        if (isSoundPlaying) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        } else {
            audioRef.current.currentTime = 0; 
            audioRef.current.play().catch(error => {
                console.error(`Failed to play audio for ${animal.name}:`, error);
            });
        }
    };
    
    // --- Function to start Video playback ---
    const handlePlayVideo = () => {
        setIsPlaying(true); // This swaps image for video
        // Optional: Also play sound when video starts
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(error => {
                console.error(`Failed to play audio with video for ${animal.name}:`, error);
            });
        }
    }
    
    // --- Function to stop Video and Sound ---
    const handleStopVideoAndSound = () => {
        setIsPlaying(false); // Hide video
        setIsFullscreen(false); // Exit fullscreen
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }


    const handleToggleFullscreen = (e) => {
        e.stopPropagation();
        setIsFullscreen(prev => !prev);
        // Ensure video is visible if fullscreen is activated
        if (!isFullscreen && !isPlaying) {
            setIsPlaying(true);
        }
    };

    // Determine card classes based on fullscreen state
    const cardClasses = isFullscreen
        ? "fixed top-0 left-0 w-full h-full z-50 rounded-none shadow-2xl transition-all duration-500"
        : "flex flex-col bg-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] border border-gray-100 overflow-hidden w-full";
    
    // Determine media container height
    const mediaHeightClass = isFullscreen ? "h-[80vh]" : "h-80 sm:h-96";


    return (
        <motion.article
            ref={cardRef}
            variants={isFullscreen ? {} : itemVariants}
            layout
            className={cardClasses}
            whileHover={isFullscreen ? {} : { y: -3 }}
            style={isFullscreen ? { backgroundColor: 'white', overflowY: 'auto' } : {}}
        >
<div className={`relative ${mediaHeightClass} overflow-hidden`}>
    <AnimatePresence mode="wait">
        {/* Video Player */}
        {isPlaying && animal.videoUrl ? (
            <motion.iframe
                key="video-player"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full h-full absolute top-0 left-0"
                src={animal.videoUrl}
                frameBorder="0"
                allow="autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${animal.name} Video`}
            />
        ) : (
            /* Static Image */
            <motion.div
                key="static-image"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full h-full bg-cover bg-center cursor-pointer"
                style={{ backgroundImage: `url(${animal.imageUrl})` }}
                onClick={handlePlayVideo}
            >
                {/* subtle dark overlay for contrast */}
                <div className="absolute inset-0 bg-black/10" />
            </motion.div>
        )}
    </AnimatePresence>

    {/* Tap to Watch Video Hint */}
    {!isPlaying && animal.videoUrl && (
        <motion.div
            onClick={handlePlayVideo}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2
                       bg-black/70 text-white text-sm font-semibold
                       px-4 py-2 rounded-full flex items-center gap-2
                       cursor-pointer z-10 shadow-lg backdrop-blur"
        >
            <PlayCircle className="w-4 h-4" />
            Tap to watch video
        </motion.div>
    )}

    {/* Difficulty Tag */}
    <div className={`absolute top-4 left-4 ${levelClasses} z-10`}>
        {animal.difficulty}
    </div>

    {/* Stop Video Button */}
    {isPlaying && (
        <motion.button
            onClick={handleStopVideoAndSound}
            className="absolute top-4 right-4 p-3 bg-gray-800/90 text-white 
                       rounded-full hover:bg-gray-900 z-10 shadow-lg"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            title="Stop Video"
        >
            <OctagonX className="w-6 h-6" />
        </motion.button>
    )}
</div>


            <div className="p-5 flex flex-col flex-grow">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">{animal.name}</h2>
                    <Volume2 className="w-6 h-6 text-orange-500" />
                </div>
                
                <p className="text-gray-500 text-sm mb-4 flex-grow">{animal.description}</p>

                <p className="text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full inline-block self-start mb-4">
                    Type: {animal.type}
                </p>
            </div>
            
            {/* Play/Pause Sound Button (Now only controls audio) */}
            <motion.button 
                className={`mx-5 mb-5 py-3 text-white font-semibold text-lg rounded-lg ${buttonColor} flex items-center justify-center gap-2 transition-colors`}
                onClick={handleToggleSound} // <--- ONLY TOGGLES AUDIO
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                {/* Check if the actual audio is playing using audioRef */}
                {!audioRef.current || audioRef.current.paused ? (
                    <>
                        <Volume2 className="w-5 h-5" />
                        Listen to Sound 
                    </>
                ) : (
                    <>
                        <PauseCircle className="w-5 h-5" />
                        Stop Audio
                    </>
                )}
            </motion.button>
        </motion.article>
    );
};


// --- Main Component (Only referencing the updated card) ---
export default function AnimalSoundExplorer() {
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState("All Animals");

    const filteredAnimalSounds = useMemo(() => {
        const categoryFilter = ANIMAL_CATEGORIES.find(cat => cat.name === activeCategory)?.filterKey;

        return ALL_ANIMAL_SOUNDS.filter((a) => {
            const categoryMatch = categoryFilter === null || a.type === categoryFilter;
            const searchMatch =
                a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.habitat.toLowerCase().includes(searchTerm.toLowerCase());
            return categoryMatch && searchMatch;
        });
    }, [searchTerm, activeCategory]);

    const resetFilters = () => {
        setSearchTerm("");
        setActiveCategory("All Animals");
    };


    return (
        <div className="min-h-screen w-full bg-gray-50 text-gray-800 overflow-x-hidden">
            
            {/* Header/Search Bar (Same as before) */}
            <header className="sticky top-0 z-20 w-full bg-white shadow-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h1 className="text-3xl font-extrabold text-teal-600 flex items-center gap-2">
                        <PawPrint className="w-8 h-8 text-orange-500" />
                        Animal Sound Explorer
                    </h1>
                    <div className="relative w-full max-w-lg hidden sm:block">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search animals, habitats, or sounds..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full py-2.5 pl-12 pr-12 text-base rounded-full border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all duration-200"
                        />
                        {(searchTerm || activeCategory !== "All Animals") && (
                            <motion.button
                                onClick={resetFilters}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-500 hover:text-red-500"
                                title="Clear Filters"
                                whileTap={{ scale: 0.8 }}
                            >
                                <X className="w-5 h-5" />
                            </motion.button>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content: Sidebar and Grid (Same as before) */}
            <div className="flex flex-col lg:flex-row w-full max-w-full">
                
                <aside className="w-full lg:w-64 flex-shrink-0 p-6 bg-white border-r border-gray-200 lg:h-screen lg:sticky lg:top-[5.5rem] shadow-xl lg:shadow-none">
                    <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2 border-b pb-2">
                        <Filter className="w-5 h-5 text-teal-500" />
                        Filter by Type
                    </h2>
                    
                    <nav className="flex flex-wrap lg:flex-col gap-2">
                        {ANIMAL_CATEGORIES.map((cat) => {
                            const isActive = activeCategory === cat.name;
                            const Icon = cat.icon;
                            return (
                                <motion.button
                                    key={cat.name}
                                    onClick={() => setActiveCategory(cat.name)}
                                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center gap-3 font-semibold text-sm transition-colors duration-200`}
                                    variants={sidebarItemVariants}
                                    animate={isActive ? "active" : "inactive"}
                                    whileHover={{ scale: 1.02, backgroundColor: "rgba(243, 244, 246, 1)" }}
                                >
                                    <Icon className="w-5 h-5 flex-shrink-0" />
                                    {cat.name}
                                    {isActive && <CheckCircle className="w-4 h-4 text-teal-500 ml-auto" />}
                                </motion.button>
                            );
                        })}
                    </nav>
                </aside>

                <main className="flex-grow p-6 sm:p-8">
                    <h2 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">
                        {activeCategory}
                        <span className="text-teal-500 ml-2">Sounds</span>
                    </h2>
                    
                    <AnimatePresence mode="wait">
                        {filteredAnimalSounds.length > 0 ? (
                            <motion.div
                                key="animal-grid"
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                            >
                                <AnimatePresence>
                                    {filteredAnimalSounds.map((a) => (
                                        // Uses the updated AnimalSoundCard
                                        <AnimalSoundCard key={a.id} animal={a} />
                                    ))}
                                </AnimatePresence>
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="no-results"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="text-center py-16 bg-white rounded-xl shadow-inner border border-red-200 mt-6"
                            >
                                <Search className="w-12 h-12 text-red-400 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-700 mb-2">Search Did Not Match</h3>
                                <p className="text-lg text-gray-500">
                                    No animal sounds found in the {activeCategory} category matching "{searchTerm}".
                                </p>
                                <button
                                    onClick={resetFilters}
                                    className="mt-6 px-6 py-2 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors shadow-md"
                                >
                                    View All Animal Sounds
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </main>
            </div>
        </div>
    );
}